import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalTitle,
  Skeleton,
} from '@insolech/ui';
import { emailNotifications } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';

/**
 * EmailNotificationsTab - the podiatrist's own control over notification
 * emails (migration 20270328000000).
 *
 * Everything starts OFF; enabling is always an explicit act, here or by the
 * admin in the matrix (same dataset, either side can flip it back). Per email:
 * on/off, recipients (login address by default; added addresses receive
 * NOTHING until their owner confirms the verification link), and a read-only
 * rendered preview of the template.
 */

function errorText(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: unknown }).message);
  return String(e);
}

export function EmailNotificationsTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userId = user?.id ?? '';

  const [pageError, setPageError] = React.useState<string | null>(null);
  const [addOpenFor, setAddOpenFor] = React.useState<string | null>(null);
  const [newAddress, setNewAddress] = React.useState('');
  const [addNotice, setAddNotice] = React.useState<string | null>(null);
  const [previewFor, setPreviewFor] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<{ subject: string; html: string } | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);

  const kindsQuery = useQuery(emailNotifications.emailNotificationKindsQuery(supabase));
  const mineQuery = useQuery({
    ...emailNotifications.myEmailNotificationsQuery(supabase, userId),
    enabled: !!userId,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: emailNotifications.emailNotificationKeys.mine(userId) });

  const toggleMut = useMutation({
    mutationFn: (args: { kind: string; enabled: boolean }) =>
      emailNotifications.setEmailSubscription(supabase, { userId, ...args }),
    onSuccess: () => void invalidate(),
    onError: (e) => setPageError(errorText(e)),
  });

  const addMut = useMutation({
    mutationFn: (args: { kind: string; address: string }) =>
      emailNotifications.addEmailRecipient(supabase, args),
    onSuccess: (res) => {
      void invalidate();
      setNewAddress('');
      setAddOpenFor(null);
      setAddNotice(
        res.verified
          ? 'Address added. It is your own login address, so it is already verified.'
          : 'Address added as PENDING. Its owner must click the confirmation link before anything is sent there.' +
              (res.verification_posted
                ? ''
                : ' The confirmation email will go out once the email system is armed; use Resend then.'),
      );
    },
    onError: (e) => setPageError(errorText(e)),
  });

  const resendMut = useMutation({
    mutationFn: (id: string) => emailNotifications.resendRecipientVerification(supabase, id),
    onSuccess: (posted) => {
      void invalidate();
      setAddNotice(
        posted
          ? 'Verification email sent again.'
          : 'Verification could not be sent (the email system is not armed yet).',
      );
    },
    onError: (e) => setPageError(errorText(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => emailNotifications.deleteEmailRecipient(supabase, id),
    onSuccess: () => void invalidate(),
    onError: (e) => setPageError(errorText(e)),
  });

  const openPreview = async (templateKey: string) => {
    setPreviewFor(templateKey);
    setPreview(null);
    setPreviewError(null);
    try {
      setPreview(await emailNotifications.fetchEmailTemplatePreview(supabase, templateKey));
    } catch (e) {
      setPreviewError(errorText(e));
    }
  };

  if (kindsQuery.isLoading || mineQuery.isLoading) return <Skeleton className="h-48 w-full" />;
  if (kindsQuery.isError || mineQuery.isError) {
    return (
      <Alert tone="error" title="Could not load notification settings">
        {errorText(kindsQuery.error ?? mineQuery.error)}
      </Alert>
    );
  }

  const kinds = kindsQuery.data ?? [];
  const subs = mineQuery.data?.subscriptions ?? [];
  const recipients = mineQuery.data?.recipients ?? [];

  return (
    <div className="space-y-4">
      {pageError ? (
        <Alert tone="error" title="Change failed">
          {pageError}
        </Alert>
      ) : null}
      {addNotice ? <Alert tone="info" title="Recipients">{addNotice}</Alert> : null}

      {kinds.map((k) => {
        const enabled = subs.find((s) => s.kind === k.kind)?.enabled ?? false;
        const kindRecipients = recipients.filter((r) => r.kind === k.kind);
        return (
          <Card key={k.kind}>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle>{k.title}</CardTitle>
                  <CardDescription>
                    {k.description} <span className="text-text-muted">({k.cadence_label})</span>
                  </CardDescription>
                </div>
                <label className="flex shrink-0 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border-default text-brand-600 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    checked={enabled ?? false}
                    disabled={toggleMut.isPending}
                    aria-label={`Receive ${k.title} emails`}
                    onChange={(e) => toggleMut.mutate({ kind: k.kind, enabled: e.target.checked })}
                  />
                  {enabled ? 'On' : 'Off'}
                </label>
              </div>
            </CardHeader>
            <CardBody className="space-y-3">
              <div>
                <div className="mb-1 text-sm font-medium">Recipients</div>
                <p className="mb-2 text-xs text-text-muted">
                  Goes to your login address <span className="font-medium">{user?.email}</span>
                  {kindRecipients.some((r) => r.is_primary && r.verified_at)
                    ? ' unless a verified primary below replaces it'
                    : ''}
                  . Added addresses receive nothing until their owner confirms the verification
                  email.
                </p>
                {kindRecipients.length > 0 ? (
                  <ul className="mb-2 space-y-1 text-sm">
                    {kindRecipients.map((r) => (
                      <li key={r.id} className="flex items-center gap-2">
                        <span className="font-medium">{r.address}</span>
                        {r.is_primary ? <span className="text-xs text-text-muted">primary</span> : null}
                        {r.verified_at ? (
                          <span className="text-xs text-success-700">Verified</span>
                        ) : (
                          <span className="text-xs text-warning-700">Pending</span>
                        )}
                        {!r.verified_at ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            startIcon={<RefreshCw className="h-3 w-3" />}
                            disabled={resendMut.isPending}
                            onClick={() => resendMut.mutate(r.id)}
                          >
                            Resend
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="secondary"
                          startIcon={<Trash2 className="h-3 w-3" />}
                          disabled={deleteMut.isPending}
                          onClick={() => deleteMut.mutate(r.id)}
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {addOpenFor === k.kind ? (
                  <form
                    className="flex items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const address = newAddress.trim();
                      if (address) addMut.mutate({ kind: k.kind, address });
                    }}
                  >
                    <Input
                      value={newAddress}
                      onChange={(e) => setNewAddress(e.target.value)}
                      placeholder="name@example.com"
                      type="email"
                      aria-label="New recipient address"
                    />
                    <Button type="submit" size="sm" disabled={addMut.isPending}>
                      {addMut.isPending ? 'Adding…' : 'Add'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setAddOpenFor(null);
                        setNewAddress('');
                      }}
                    >
                      Cancel
                    </Button>
                  </form>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    startIcon={<Plus className="h-4 w-4" />}
                    onClick={() => {
                      setAddOpenFor(k.kind);
                      setAddNotice(null);
                    }}
                  >
                    Add an address
                  </Button>
                )}
              </div>
              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  startIcon={<Eye className="h-4 w-4" />}
                  onClick={() => void openPreview(k.template_key)}
                >
                  View the email template
                </Button>
              </div>
            </CardBody>
          </Card>
        );
      })}

      {/* Read-only template preview */}
      <Modal open={previewFor !== null} onOpenChange={(v) => (!v ? setPreviewFor(null) : undefined)}>
        <ModalContent className="max-w-2xl">
          <ModalHeader>
            <ModalTitle>{preview?.subject ?? 'Template preview'}</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {previewError ? (
              <Alert tone="error" title="Preview unavailable">
                {previewError}
              </Alert>
            ) : preview ? (
              // Server-rendered with server-defined sample values; sandboxed
              // so the email HTML cannot run anything in the app context.
              <iframe
                title="Email template preview"
                sandbox=""
                srcDoc={preview.html}
                className="h-96 w-full rounded border border-border-subtle bg-white"
              />
            ) : (
              <Skeleton className="h-96 w-full" />
            )}
            <p className="mt-2 text-xs text-text-muted">
              This is a sample rendering with example data. Template content is managed by INSOLECH.
            </p>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}
